import { useState } from 'react'
import { motion } from 'framer-motion'
import { Briefcase, Check, HeartPulse, RefreshCw, Scale, Star, Users } from 'lucide-react'

import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'
import { LifestyleImage, LIFESTYLE_IMAGES } from '../ui'

interface LifeContextTemplateProps {
  stage: WorkflowStage
  initialValue?: string[]
  onSelect: (value: AnswerValue) => void
  onSkip?: () => void
  currentPhase?: PhaseInfo | null
}

const CONTEXT_OPTIONS = [
  { id: 'busy_career', label: 'Busy career season', icon: Briefcase },
  { id: 'focused_family', label: 'Focused on family', icon: Users },
  { id: 'transition', label: 'Going through a transition', icon: RefreshCw },
  { id: 'making_space', label: 'Making space for myself', icon: Star },
  { id: 'things_feel_off', label: 'Noticing things feel off', icon: Scale },
  { id: 'health_changes', label: 'Managing health changes', icon: HeartPulse },
]

export default function LifeContextTemplate({
  initialValue,
  onSelect,
  onSkip,
  currentPhase,
}: LifeContextTemplateProps) {
  const [selectedContexts, setSelectedContexts] = useState<string[]>(initialValue || [])

  const handleContextToggle = (contextId: string) => {
    setSelectedContexts((prev) =>
      prev.includes(contextId) ? prev.filter((id) => id !== contextId) : [...prev, contextId],
    )
  }

  const handleContinue = () => {
    onSelect({ life_contexts: selectedContexts, skipped: false })
  }

  const handleSkip = () => {
    if (onSkip) {
      onSkip()
    } else {
      onSelect({ life_contexts: [], skipped: true })
    }
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="mb-6">
        <LifestyleImage
          src={LIFESTYLE_IMAGES.lifeContext.src}
          alt={LIFESTYLE_IMAGES.lifeContext.alt}
          size="md"
          aspectRatio="wide"
          gradient
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8 grid grid-cols-2 gap-3"
      >
        {CONTEXT_OPTIONS.map((context, index) => {
          const Icon = context.icon
          const isSelected = selectedContexts.includes(context.id)
          return (
            <motion.button
              key={context.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              onClick={() => handleContextToggle(context.id)}
              className={`relative flex flex-col gap-3 rounded-xl border p-4 text-left transition-all duration-300 ${
                isSelected
                  ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                  : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'
              }`}
            >
              {isSelected && (
                <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--ww-accent)]">
                  <Check className="h-3 w-3 text-white" />
                </div>
              )}
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
                  isSelected ? 'bg-[rgba(74,138,154,0.2)]' : 'bg-[var(--ww-surface-2)]'
                }`}
              >
                <Icon className={`h-5 w-5 ${isSelected ? 'text-[var(--ww-accent)]' : 'text-[var(--ww-text-3)]'}`} />
              </div>
              <span className="pr-6 text-sm font-medium leading-tight">{context.label}</span>
            </motion.button>
          )
        })}
      </motion.div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)] space-y-3">
        <button
          onClick={handleContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:bg-[var(--ww-btn-hover)] hover:-translate-y-0.5"
        >
          Continue
        </button>
        <button
          onClick={handleSkip}
          className="w-full py-3 text-sm font-medium text-[var(--ww-text-3)] transition-colors hover:text-[var(--ww-text)]"
        >
          Skip this question
        </button>
      </div>
    </div>
  )
}
